import { toast } from 'sonner';
import { EllipsisVertical, Star, Check } from 'lucide-react';
import { BiCommentDetail } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';
import { memo, useState } from 'react';
import { PiPuzzlePieceFill } from 'react-icons/pi';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { IPostVisibility } from '@/redux/post/post.d';
import { cn, getRelativeTime } from '@/lib/utils';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import IconTooltip from '@/components/ui/icon-tooltip';
import StoreIcon, {
  dedupeByHost,
  storeHostOf,
  ResolvedLinkHost,
} from '@/components/common/StoreIcon';
import { IPartnerData } from '@/redux/partner/partner';
import { ICategory } from '@/redux/category/category';
import ProfilePicture from '../ui/profile-picture';
import ProjectGroupLikeButton from '@/redux/like/ProjectGroupLikeButton';
import ProjectGroupBookmarkButton from '@/redux/bookmark/ProjectGroupBookmarkButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PostStatusBadge from '../Post/PostStatusBadge';
import PostMeta from '../Post/PostMeta';
import { useDeleteProjectGroupMutation } from '@/redux/projectGroup/projectGroup.api.slice';
import RatingButton from '@/redux/projectMake/RatingButton';

interface IProjectGroupCardProps {
  id: string;
  name: string;
  description: string;
  thumbnail: string | null;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
  hasLikedByUser: boolean | undefined;
  hasBookmarkedByUser: boolean | undefined;
  isOwner: boolean;
  authorUsername: string;
  authorName: string;
  authorAvatar: string;
  visibility?: string;
  isOnefinityOfficial?: boolean;
  isTrustedMember?: boolean;
  partnerData?: IPartnerData;
  category?: ICategory;
  difficulty?: string | null;
  averageTotalRating?: number | null;
  totalMakes?: number;
  materialLinks?: string[];
  materialLinkHosts?: ResolvedLinkHost[];
  materialUsed?: string | null;
  youtubeUrl?: string | null;
}

function ProjectGroupCard({
  id,
  name,
  description,
  thumbnail,
  likesCount,
  commentsCount,
  createdAt,
  hasLikedByUser,
  hasBookmarkedByUser,
  isOwner,
  authorUsername,
  authorName,
  authorAvatar,
  visibility,
  isOnefinityOfficial,
  isTrustedMember,
  partnerData,
  category,
  difficulty,
  averageTotalRating,
  totalMakes,
  materialLinks,
  materialLinkHosts,
  materialUsed,
  youtubeUrl,
}: IProjectGroupCardProps) {
  const navigate = useNavigate();
  const [deleteProjectGroup] = useDeleteProjectGroupMutation();

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!groupToDelete) return;

    try {
      await deleteProjectGroup(groupToDelete).unwrap();
      toast.success('Multi Workpiece deleted');
    } catch (error) {
      toast.error('Failed to delete the Multi Workpiece. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const confirmDelete = (groupId: string) => {
    setGroupToDelete(groupId);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (groupId: string) => {
    navigate(`/project/v2/group/${groupId}/edit`);
  };

  const handleDialogClose = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const thumbnailUrl =
    thumbnail || 'https://via.placeholder.com/400x300?text=Project+Group';

  return (
    <>
      <Card className="w-full h-full min-w-0 flex flex-col bg-white border-gray-400 border-[1.5px] overflow-hidden animate-in fade-in duration-200">
        <div className="flex justify-between flex-row items-center p-3">
          <Link to={`/${authorUsername}`}>
            <div className="flex gap-3 items-center">
              <ProfilePicture
                src={authorAvatar}
                alt={authorName}
                username={authorUsername}
                variant="badge"
              />
              <div className="flex flex-col">
                <div className="flex gap-2 items-center">
                  <h6 className="font-primary font-medium">
                    @{authorUsername}
                  </h6>
                  <div className="flex gap-2 items-center">
                    {isOnefinityOfficial && (
                      <IconTooltip legendKey="onefinity-official">
                        <img
                          src={OnefinityOfficialIcon}
                          className="h-6 w-6"
                          alt="onefinity official"
                        />
                      </IconTooltip>
                    )}
                    {isTrustedMember && (
                      <IconTooltip legendKey="trusted-member">
                        <div
                          className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center"
                          aria-label="Trusted member"
                        >
                          <Check
                            className="h-3 w-3 text-white"
                            strokeWidth={3}
                          />
                        </div>
                      </IconTooltip>
                    )}
                    {partnerData && (
                      <IconTooltip
                        legendKey="partner"
                        label={`${partnerData.name ?? 'Partner'} Badge`}
                      >
                        <div
                          className={cn(
                            'h-6 w-6 rounded-full flex items-center justify-center'
                          )}
                          style={{ backgroundColor: partnerData.color }}
                        >
                          <Star className="h-3 w-3 text-white" fill="white" />
                        </div>
                      </IconTooltip>
                    )}
                  </div>
                </div>
                <p className="font-secondary text-[#666666]">
                  {getRelativeTime(createdAt)}
                </p>
              </div>
            </div>
          </Link>
          {isOwner ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleEdit(id)}>
                  Edit Group
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => confirmDelete(id)}
                  className="focus:text-red-500 focus:bg-red-200"
                >
                  Delete Group
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <ProjectGroupBookmarkButton
              bookmarked={hasBookmarkedByUser ?? false}
              projectGroupId={id}
              size="icon"
            />
          )}
        </div>
        <Link to={`/project-groups/${id}`} className="flex flex-col flex-1">
          <div className="w-full h-[192px] flex items-center justify-center bg-gray-50 relative overflow-hidden">
            {/* TODO: add a CDN/CloudFront image-transform helper to request
                card-sized width/quality variants and emit `srcset`. No such
                helper exists in the frontend yet, so we keep native lazy
                loading rather than guessing at a URL format. */}
            <img
              src={thumbnailUrl}
              alt={name}
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="flex flex-col justify-between p-5">
            <div className="flex flex-col justify-between gap-[16px]">
              <div className="flex flex-col gap-[8px]">
                <div className="flex justify-between items-center gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <IconTooltip legendKey="multi-piece-project">
                      <PiPuzzlePieceFill className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    </IconTooltip>
                    {dedupeByHost(materialLinks ?? [], materialLinkHosts).map(
                      (link) => (
                        <IconTooltip
                          key={link}
                          legendKey="dimensional-material"
                        >
                          <StoreIcon
                            url={link}
                            host={storeHostOf(link, materialLinkHosts)}
                          />
                        </IconTooltip>
                      )
                    )}
                    <h5 className="text-lg font-medium leading-snug font-primary capitalize line-clamp-2 min-w-0 flex-1">
                      {name}
                    </h5>
                  </div>
                  {visibility ? (
                    <PostStatusBadge
                      visibility={visibility as IPostVisibility}
                    />
                  ) : (
                    <div />
                  )}
                </div>
              </div>
              <p className="text-sm line-clamp-2 font-tertiary font-normal">
                {description}
              </p>
              <PostMeta
                skillLevel={difficulty ?? undefined}
                material={materialUsed ?? undefined}
                category={category?.name}
                youtubeUrl={youtubeUrl}
              />
            </div>
          </div>
        </Link>

        <div className="flex gap-3 px-5 pb-5">
          <ProjectGroupLikeButton
            likes={likesCount}
            liked={hasLikedByUser}
            projectGroupId={id}
            className="flex-1"
          />
          <Button variant="outline" size="sm" className="flex-1">
            <div className="flex items-center gap-4 justify-center text-[13px]">
              <BiCommentDetail size="16px" />
              <p>{commentsCount}</p>
            </div>
          </Button>
          <RatingButton
            averageTotalRating={averageTotalRating ?? undefined}
            totalMakes={totalMakes}
            className="flex-1"
          />
        </div>
      </Card>

      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this Multi Workpiece? The
              individual Workpieces will not be deleted, only ungrouped.
            </p>
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Memoized so cards don't re-render when an unrelated parent state changes.
export default memo(ProjectGroupCard);
